import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { customBilling } from '@/api/customBilling';
import type { CustomInvoiceRow, CustomInvoiceStatus } from '@/api/customBilling';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import {
  InvoiceStatusPill, formatMoney, formatDateOnly, formatDateTime,
} from './customInvoiceShared';

/**
 * Customer invoices — the reseller→customer invoices produced by the
 * custom-billing engine (billable items × pricing rules, rolled up per
 * period). Reseller-and-up see every customer; customer roles are scoped
 * to their own account server-side. Rows link to the invoice detail.
 */
export function CustomInvoicesPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<CustomInvoiceStatus | 'all'>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['customInvoices', statusFilter],
    queryFn: () => customBilling.listInvoices({
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: 200,
    }),
  });
  const rows = data?.rows ?? [];

  const columns = [
    { header: 'Invoice', cell: (r: CustomInvoiceRow) => (
      <span className="font-mono text-xs font-semibold text-slate-700">{r.number}</span>
    ) },
    { header: 'Customer', cell: (r: CustomInvoiceRow) => (
      <div className="flex flex-col">
        <span className="text-sm font-medium">{r.customer_name ?? '—'}</span>
        {r.customer_number != null && (
          <span className="text-[11px] text-slate-400 font-mono">#{String(r.customer_number).padStart(4, '0')}</span>
        )}
      </div>
    ) },
    { header: 'Period', cell: (r: CustomInvoiceRow) => (
      <span className="text-xs text-slate-500 whitespace-nowrap">
        {formatDateOnly(r.period_start)} – {formatDateOnly(r.period_end)}
      </span>
    ) },
    { header: 'Total', headClassName: 'text-end', className: 'text-end', cell: (r: CustomInvoiceRow) => (
      <span className="font-mono text-sm tabular-nums font-semibold">{formatMoney(r.total_cents)}</span>
    ) },
    { header: 'Status', cell: (r: CustomInvoiceRow) => <InvoiceStatusPill status={r.status} /> },
    { header: 'Issued', cell: (r: CustomInvoiceRow) => (
      <span className="text-xs text-slate-500">{formatDateTime(r.issued_at)}</span>
    ) },
    { header: '', className: 'w-8 text-end', cell: () => (
      <ChevronRight className="h-4 w-4 text-slate-300" aria-hidden="true" />
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Customer invoices"
        description="Invoices issued to customers by the custom-billing engine — period roll-ups of billable items priced by your pricing rules."
        actions={
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="h-9 w-36 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
            <Button asChild variant="outline" size="sm">
              <Link to="/billing"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to billing</Link>
            </Button>
          </div>
        }
      />
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading invoices…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load invoices.'}
        </div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          onRowClick={(r) => navigate(`/billing/custom-invoices/${r.id}`)}
          empty="No customer invoices yet. Invoices appear here after the billing engine rolls up a period (Settings → Billable items / Pricing rules)."
        />
      )}
    </>
  );
}
